import { modelState, ModelStateList } from "@/utils/modelState";
import { dateEqualityComparer, multiplyAndRemoveDecimal, stringIsNullOrEmpty } from "@/utils/utils";
import { add, startOfDay, sub } from 'date-fns';
import _ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import { ProgramBookingState } from "./model.Programs";
import type { ReservationCompanyCache } from "./model.ReservationCompany";
import { isReservationExistingPerson, ReservationPersonCache } from "./model.ReservationPerson";
import { buildSaveReservationRequest } from "./model.Reservations";
import type { ReservationState as BaseReservationState, NewCompanyDto, ReservationContact, RoomBookingState } from "./types";
import {
    AvailabilityCheckResult,
    CheckPriceResult
} from "./types";

// Extend the base ReservationState to include PersonCache and error handling
export interface ReservationState extends BaseReservationState {
    // Optional billing contact (falls back to Person when null)
    BillingContact?: modelState<ReservationContact | null>;
    PersonCache: ReservationPersonCache;
    // Company cache + session companies (mirror BaseReservationState, make explicit here)
    CompanyCache?: ReservationCompanyCache;
    NewCompanies?: ModelStateList<NewCompanyDto>;
    LoadingError: modelState<string | null>;
    HasCriticalError: modelState<boolean>;
}

export function updateCanRelease(reservation: ReservationState) {
    const canRelease = stringIsNullOrEmpty(reservation.ReservationId) ||
        (reservation.ArrivalDate.value > new Date() &&
            reservation.Payments.toArray().filter(p => p.Amount > 0).length === 0 &&
            reservation.Refunds.toArray().filter(r => r.Amount > 0).length === 0 &&
            reservation.PosItems.value.length === 0);
    reservation.CanRelease.setValue(canRelease);
}

export function updateAmountPayableTotal(reservation: ReservationState) {
    const amountPayableTotal = calculateAmountPayableTotal(reservation);
    reservation.AmountPayableTotal.setValue(amountPayableTotal);
}

export function calculateAmountPayableTotal(reservation: ReservationState) {
    const roomBookingTotal = _.sum(reservation.RoomBookings.toArray()
        .map(rb => multiplyAndRemoveDecimal(rb.CalculatedPrice.value.totalPayableAmount))) / 100;
    const programBookingTotal = _.sum(reservation.ProgramBookings.toArray().map(pb => multiplyAndRemoveDecimal(pb.CalculatedPrice.value.totalPayableAmount))) / 100;
    const posItemsTotal = _.sum(reservation.PosItems.value.map(pi => multiplyAndRemoveDecimal(pi.Amount) * pi.Quantity)) / 100;
    const multiplyCalculated = multiplyAndRemoveDecimal(roomBookingTotal) + multiplyAndRemoveDecimal(programBookingTotal) + multiplyAndRemoveDecimal(posItemsTotal);
    return multiplyCalculated / 100;
}

export function updateAmountDueTotal(reservation: ReservationState) {
    const amountDueTotal = calculateAmountDueTotal(reservation);
    reservation.AmountDueTotal.setValue(amountDueTotal);
}

export function calculateAmountDueTotal(reservation: ReservationState) {
    const roomBookingTotal = _.sum(reservation.RoomBookings.toArray()
        .map(rb => multiplyAndRemoveDecimal(rb.CalculatedPrice.value.totalPayableAmount))) / 100;
    const programBookingTotal = _.sum(reservation.ProgramBookings.toArray().map(pb => multiplyAndRemoveDecimal(pb.CalculatedPrice.value.totalPayableAmount))) / 100;
    const posItemsTotal = _.sum(reservation.PosItems.value.map(pi => multiplyAndRemoveDecimal(pi.Amount) * pi.Quantity)) / 100;
    const paymentsAmountTotal = _.sum(reservation.Payments.toArray().map(p => multiplyAndRemoveDecimal(p.Amount))) / 100;
    const refundsAmountTotal = _.sum(reservation.Refunds.toArray().map(r => multiplyAndRemoveDecimal(r.Amount))) / 100;
    const multiplyCalculated = (multiplyAndRemoveDecimal(roomBookingTotal) +
        multiplyAndRemoveDecimal(programBookingTotal) +
        multiplyAndRemoveDecimal(posItemsTotal)) -
        (multiplyAndRemoveDecimal(paymentsAmountTotal) -
            multiplyAndRemoveDecimal(refundsAmountTotal));
    return multiplyCalculated / 100;
}

export function calculateDates(roomBookings: RoomBookingState[], programBookings: ProgramBookingState[], fallbackStart: Date, fallbackEnd: Date) {
    if (roomBookings.length <= 0 && programBookings.length <= 0) {
        return {
            arrivalDate: fallbackStart,
            departureDate: fallbackEnd,
            datesEnabled: true
        };
    }
    const checkInDates = roomBookings.map(rb => startOfDay(rb.CheckIn.value));
    const checkOutDates = roomBookings.map(rb => startOfDay(rb.CheckOut.value));
    const startDates = programBookings.map(pb => startOfDay(pb.Start.value));
    const endDates = programBookings.map(pb => startOfDay(pb.End.value));
    const arrivalDates = roomBookings.length > 0
        ? [...checkInDates, ...startDates.map(pb => sub(pb, { days: 1 }))]
        : [...checkInDates, ...startDates];
    const departureDates = [...checkOutDates, ...endDates];
    const arrivalDate = new Date(_.min([...checkInDates.map(d => d.getTime()), ...startDates.map(d => d.getTime())]));
    const departureDate = new Date(_.max([...checkOutDates.map(d => d.getTime()), ...endDates.map(d => d.getTime())]));
    const datesEnabled = _.uniq(arrivalDates.map(d => d.getTime())).length === 1 && _.uniq(departureDates.map(d => d.getTime())).length === 1;
    return {
        arrivalDate: arrivalDate,
        departureDate: departureDate,
        datesEnabled: datesEnabled
    };
}

export function updateStayDates(reservation: ReservationState) {
    const dates = calculateDates(reservation.RoomBookings.toArray(), reservation.ProgramBookings.toArray(), reservation.ArrivalDate.value, reservation.DepartureDate.value);
    reservation.ArrivalDateCalculated.setValue(dates.arrivalDate);
    reservation.DepartureDateCalculated.setValue(dates.departureDate);
    reservation.DatesEnabled.setValue(dates.datesEnabled);
    if (dates.datesEnabled) {
        reservation.ArrivalDate.setValue(dates.arrivalDate);
        reservation.DepartureDate.setValue(dates.departureDate);
    }
}

export function createRoomBookingPriceChecker(reservation: ReservationState, roomBooking: RoomBookingState) {
    const controller = new AbortController();
    return {
        price: roomBooking.Price.price.value,
        dates: roomBooking.Dates,
        isLoading: modelState(false),
        refresh: async function () {
            if (!roomBooking.RoomType.value || roomBooking.RoomType.value === "unassigned") {
                this.isLoading.setValue(true);
                this.price = {
                    baseAmount: 0,
                    payableAmount: null
                };
                this.isLoading.setValue(false);
                return;
            }
            if (this.isLoading.value) {
                controller.abort('cancelled');
            }
            else {
                this.isLoading.setValue(true);
            }
            const request = buildSaveReservationRequest(reservation);
            let response: Response;
            try {
                response = await fetch(`/reservations-price-check`, {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8'
                    },
                    body: JSON.stringify(request)
                });
            }
            catch (ex) {
                if (ex === "cancelled") {
                    return;
                }
                else {
                    throw ex;
                }
            }
            const result = await response.json() as CheckPriceResult;
            const room = result.rooms.find(r => r.reservationRoomId === roomBooking.ReservationRoomId);
            this.price = room?.price;
            this.dates = (room?.dates ?? []).map(d => {
                return {
                    Date: new Date(d.Date as any),
                    //BaseAmount: d.BaseAmount,
                    //PayableAmount: d.PayableAmount,
                    Vouchers: d.Vouchers
                };
            });
            this.isLoading.setValue(false);
        }
    };
}

export interface RoomBookingPriceCheckerRequest {
    roomType: string;
    checkIn: string;
    checkOut: string;
    vouchers: string[];
}

export function addRoomBooking(reservation: ReservationState) {
    let checkIn = reservation.ArrivalDateCalculated.value;
    const programs = reservation.ProgramBookings.toArray();
    if (programs.length > 0) {
        const programMin = _.min(programs.map(pb => pb.Start.value.getTime()));
        checkIn = sub(new Date(programMin), { days: 1 });
    }
    const roomType = modelState(null);
    checkIn = add(startOfDay(checkIn), { hours: 14 });
    const checkOut = add(startOfDay(reservation.DepartureDateCalculated.value), { hours: 12 });
    const defaultPeople = getDefaultPeople(reservation);
    const people = defaultPeople.map(p => ({
        ReservationRoomPersonId: uuidv4(),
        Person: modelState(p),
        CheckIn: modelState(checkIn),
        CheckOut: modelState(checkOut),
        IsDirty: modelState(true)
    }));
    reservation.RoomBookings.add({
        //DataLoader: createRoomBookingDataLoader(checkIn, checkOut, roomType),
        ReservationRoomId: uuidv4(),
        RoomType: roomType,
        RoomLocationId: modelState('unassigned'),
        CheckIn: modelState(checkIn, dateEqualityComparer),
        CheckOut: modelState(checkOut, dateEqualityComparer),
        Adults: modelState(0),
        Children: modelState(0),
        Infants: modelState(0),
        IsDirty: modelState(false),
        Price: {
            price: modelState({
                baseAmount: 0,
                payableAmount: null
            }),
            isLoading: modelState(false)
        },
        CalculatedPrice: modelState({
            breakdowns: [],
            totalBaseAmount: 0,
            totalPayableAmount: 0
        }),
        Dates: [],
        People: new ModelStateList(people),
        SomePeopleDirty: modelState(false),
        PeopleVersion: modelState(0),
        NoShow: modelState(false),
        NoShowDirty: modelState(false),
        NoShowVersion: modelState(0),
        NoCharge: modelState(false),
        NoChargeDirty: modelState(false),
        NoChargeVersion: modelState(0),
        SplitFromReservationRoomId: null,
        SplitMode: null,
        DatesUIVersion: modelState(0),
        RatePlanOverride: modelState(null),
        BaseRateOverride: modelState(null),
        RecalculateStayPeriod: modelState(null),
        UseOriginalRatePlan: modelState(null),
        AppliedVouchers: modelState([]),
        RoomBlocks: modelState([]),
        RoomMovements: modelState(0),
        LockRate: modelState(false),
        Recalculate: modelState(false),
        AvailabilityCheckResult: modelState<AvailabilityCheckResult>({
            IsAvailable: true,
            IsBlockedByTempReservation: false,
            BlockedByTempReservationNumbers: []
        }),
        AdditionalCharges: modelState([]),
        OpenPrice: modelState<number | null>(null),
        OpenPriceDescription: modelState<string | null>(null),
        IgnoreVouchers: modelState(false),
        IsExisting: false,
        ForceUnlocked: modelState(false),
        RoomFormResponseJson: modelState<string | null>(null),
        RoomFormFieldsMissingMandatoryNames: modelState<string[]>([])
    });
}

export function getDefaultPeople(reservation: ReservationState) {
    if (reservation.Person.value &&
        isReservationExistingPerson(reservation.Person.value)) {
        return [reservation.Person.value];
    }
    return [];
}
